import { useHallUsers } from '@/contexts/HallUsersProvider'
import { SpeakingRole } from '@db8/shared'
import {
  DndContext,
  DragOverlay,
  rectIntersection,
  type DragStartEvent,
  type DragEndEvent,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
} from '@dnd-kit/core'
import DroppableContainer from '@/components/DroppableContainer'
import { DraggableParticipantListItem, ParticipantListItem } from './ParticipantListItem'
import { useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useTurns } from '@/contexts/TurnsProvider'
import { useHall } from '@/contexts/HallProvider'
import { useIOSScrollFix } from '@/hooks/useIOSScrollFix'

const ROLE_IDS = ['moderators', 'affirmative', 'negative', 'spectators'] as const
type RoleId = (typeof ROLE_IDS)[number]

function roleOfUser(u: any): RoleId {
  if (u.speakingRole === SpeakingRole.Moderator) return 'moderators'
  if (u.speakingRole === SpeakingRole.Affirmative) return 'affirmative'
  if (u.speakingRole === SpeakingRole.Negative) return 'negative'
  return 'spectators'
}

export function ParticipantPanel({ panelHeight }) {
  const { hallUsers, updateHallUser, isModerator, isBot } = useHallUsers()
  const { hall } = useHall()
  const { maxMembers } = useTurns()

  const groupedUsers = useMemo(() => {
    return {
      moderators: hallUsers.filter((u) => u.speakingRole === SpeakingRole.Moderator),
      affirmative: hallUsers.filter((u) => u.speakingRole === SpeakingRole.Affirmative),
      negative: hallUsers.filter((u) => u.speakingRole === SpeakingRole.Negative),
      spectators: hallUsers.filter(
        (u) =>
          ![SpeakingRole.Moderator, SpeakingRole.Affirmative, SpeakingRole.Negative].includes(
            u.speakingRole,
          ),
      ),
    }
  }, [hallUsers])

  const [activeId, setActiveId] = useState<string | null>(null)
  const activeUser = useMemo(
    () => hallUsers.find((u) => u.id === activeId) ?? null,
    [activeId, hallUsers],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
  )

  const scrollRef = useRef<HTMLDivElement | null>(null)
  useIOSScrollFix(scrollRef)

  const resolveDropRole = (overIdRaw: string | number | symbol): RoleId | null => {
    const overId = String(overIdRaw)
    if ((ROLE_IDS as readonly string[]).includes(overId)) return overId as RoleId
    if (overId.startsWith('empty-')) {
      const maybe = overId.replace('empty-', '')
      if ((ROLE_IDS as readonly string[]).includes(maybe)) return maybe as RoleId
    }
    const otherUser = hallUsers.find((u) => u.id === overId)
    if (otherUser) return roleOfUser(otherUser)
    return null
  }

  const handleDragStart = (event: DragStartEvent) => setActiveId(String(event.active.id))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    const clearOverlay = () => setActiveId(null)

    if (!over || !isModerator) return clearOverlay()

    const user = hallUsers.find((u) => u.id === active.id)
    if (!user) return clearOverlay()

    const currentRole = roleOfUser(user)
    const newRole = resolveDropRole(over.id)
    if (!newRole || newRole === currentRole) return clearOverlay()
    if (newRole === 'moderators' && isBot(user)) return clearOverlay()

    if (
      (newRole === 'affirmative' || newRole === 'negative') &&
      groupedUsers[newRole].length >= maxMembers
    ) {
      toast(`This team is at maximum capacity.`, {
        position: 'bottom-left',
        autoClose: 2000,
        hideProgressBar: true,
        closeButton: false,
        pauseOnHover: false,
        draggable: false,
      })
      return clearOverlay()
    }

    const roleMap = {
      moderators: SpeakingRole.Moderator,
      affirmative: SpeakingRole.Affirmative,
      negative: SpeakingRole.Negative,
      spectators: SpeakingRole.None,
    } as const

    updateHallUser(user.id, { speakingRole: roleMap[newRole] })
    if (user.clerkId) {
      localStorage.setItem(`speakingRole-${user.clerkId}-${hall?.id}`, String(roleMap[newRole]))
    }
    requestAnimationFrame(clearOverlay)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex w-full flex-col bg-gray-750" style={{ height: panelHeight }}>
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto min-h-0 ios-scroll noScrollbar px-2 mt-2"
        >
          {Object.entries(groupedUsers).map(
            ([role, users]) =>
              (isModerator || users.length > 0) && (
                <DroppableContainer key={role} id={role}>
                  <div
                    className={`rounded p-2 ${
                      role === 'moderators'
                        ? 'gradient-bg-moderators text-white'
                        : role === 'affirmative'
                          ? 'gradient-bg-affirmative text-white'
                          : role === 'negative'
                            ? 'gradient-bg-negative text-white'
                            : 'text-gray-800 dark:text-white'
                    }`}
                  >
                    <div className="flex w-full mb-2 capitalize">
                      <h3 className="font-bold text-lg">
                        {role === 'affirmative'
                          ? hall?.affirmingTeamName
                          : role === 'negative'
                            ? hall?.dissentingTeamName
                            : role.replace(/([A-Z])/g, ' $1')}
                      </h3>
                      {!['spectators', 'moderators'].includes(role) && (
                        <h5 className="ml-auto text-sm font-semibold">
                          {users.length}/{maxMembers}
                        </h5>
                      )}
                    </div>

                    <div className="space-y-2">
                      {users.length > 0
                        ? users.map((u) => (
                            <DraggableParticipantListItem
                              key={u.id}
                              participantId={u.id}
                              raisedHand={false}
                              user={u}
                            />
                          ))
                        : isModerator && (
                            <div
                              id={`empty-${role}`}
                              className={`p-4 text-center ${
                                role === 'spectators' ? 'text-gray-600' : 'text-gray-100'
                              } dark:text-gray-300 font-semibold border-dashed border`}
                            >
                              Drop user here to join team
                            </div>
                          )}
                    </div>
                  </div>
                </DroppableContainer>
              ),
          )}
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeUser ? (
          <div className="w-full">
            <ParticipantListItem
              participantId={activeUser.videoSDKId || ''} // VideoSDK state in overlay
              raisedHand={false}
              user={activeUser}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
